import { Box } from "@mui/material"
import {
  FocusContext,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react"

import { Pages } from "../../../App"
import { DGText } from "../../../components/common/DGText"
import { useMixpanel } from "../../../hooks/use-mixpanel"
import useSdk, { ErrorLevel } from "../../../hooks/use-sdk"
import { useSmoothNavigate } from "../../../hooks/use-smooth-navigate"
import useTranslate from "../../../hooks/use-translate"
import { appStore } from "../../../store/AppStore"
import { comb, s } from "../../../utilities/common"
import { captureSentryException } from "../../../utilities/sentry"
import { theme, TI } from "../../../utilities/theme"
import { LoginModal } from "../../Settings/Login/LoginModal"
import { PrivacySettingsModal } from "../../Settings/PrivacySettingsModal"
import { OnboardingFocusKeys } from "../components/onboarding-focus-keys"
import { OnboardingButton } from "../components/OnboardingButton"
import { PrivacyPolicyModal } from "../PrivacyPolicyModal"

export const Slide2 = ({
  setBBOverride,
}: {
  setBBOverride: Dispatch<SetStateAction<boolean>>
}) => {
  const t = useTranslate()
  const track = useMixpanel()
  const { ref, focusKey } = useFocusable({
    isFocusBoundary: true,
  })

  const [infoOpen, setInfoOpen] = useState(false)
  const [privacyOpen, setPrivacyOpen] = useState(false)
  const [loginOpen, setLoginOpen] = useState(false)
  const navigate = useSmoothNavigate()

  useEffect(() => {
    setBBOverride(infoOpen || privacyOpen || loginOpen)
  }, [infoOpen, privacyOpen, loginOpen])

  const startLogin = useCallback(() => {
    window.localStorage.setItem("onboardingPlaceholder", "true")
    setLoginOpen(true)
    if (!loginOpen)
      track("login_start", {
        page_type: "Onboarding",
        cta_id: "onboarding__button_login_or_register",
        cta_label: t("onboarding__button_login_or_register"),
        display_context: "Modal",
      })
  }, [loginOpen, track])

  useEffect(() => setFocus(OnboardingFocusKeys.Default), [])
  return (
    <>
      <FocusContext.Provider value={focusKey}>
        <Box
          ref={ref}
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            zIndex: 2,
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
              width: s("925px"),
              alignItems: "center",
            }}
          >
            <DGText
              tx={TI.H05tv}
              sx={{
                color: theme.palette.white.main,
                mb: s("24px"),
              }}
            >
              {t("onboarding__tracking_title")}
            </DGText>
            <DGText
              tx={TI.Subheadline05tv}
              sx={{
                color: theme.palette.white.main,
              }}
            >
              {t("onboarding__tracking_text")}
            </DGText>
            <OnboardingButton
              focusKey={OnboardingFocusKeys.PrivacyPolicy}
              sx={{
                mt: s("32px"),
                width: s("432px"),
                height: s("66px"),
                pr: 0,
                pl: 0,
              }}
              onEnterPress={() => setInfoOpen(true)}
              text={t("onboarding__button_show_data_protection")}
              textColors={{
                focused: theme.palette.black.main,
                blurred: theme.palette.white[70],
              }}
              backgroundColors={{
                focused: "rgba(255,255,255,1)",
                blurred: "rgba(255,255,255,0)",
              }}
              shadow={false}
            />
          </Box>
          <ButtonRow startLogin={startLogin} setPrivacyOpen={setPrivacyOpen} />
        </Box>
      </FocusContext.Provider>
      <PrivacySettingsModal
        open={comb.ine(privacyOpen, setPrivacyOpen)}
        returnFocus={OnboardingFocusKeys.PrivacySettings}
        onClose={startLogin}
      />
      <PrivacyPolicyModal open={comb.ine(infoOpen, setInfoOpen)} />
      <LoginModal
        isOpen={loginOpen}
        close={() => {
          navigate(`/${Pages.Home}`, { replace: true })
        }}
        onSuccess={() => {
          navigate(`/${Pages.Home}`, { replace: true })
        }}
      />
    </>
  )
}

const ButtonRow = ({
  startLogin,
  setPrivacyOpen,
}: {
  startLogin: () => void
  setPrivacyOpen: (open: boolean) => void
}) => {
  const t = useTranslate()
  const { ref, focusKey } = useFocusable()
  const setTracking = appStore((state) => state.setTracking)
  const sdk = useSdk(ErrorLevel.NoError)
  const [ariaLabelPrefix, setAriaLabelPrefix] = useState(
    `${t("onboarding__tracking_title")}: ${t("onboarding__tracking_text")}. `,
  )
  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        ref={ref}
        sx={{
          display: "flex",
          flexDirection: "row",
          height: "auto",
          mt: s("48px"),
          "& > :not(:last-child)": {
            mr: s("24px"),
          },
        }}
      >
        <OnboardingButton
          focusKey={OnboardingFocusKeys.Default}
          sx={{ height: s("66px") }}
          text={t("onboarding__button_agree")}
          onBlur={() => setAriaLabelPrefix("")}
          ariaLabel={`${ariaLabelPrefix}${t("onboarding__button_agree")}`}
          onEnterPress={() => {
            setTracking({ type: "mux", value: true })
            setTracking({ type: "mixpanel", value: true })
            setTracking({ type: "sentry", value: true })
            // can't send consent tracking in onboarding because of missing auth header
            startLogin()
          }}
        />
        <OnboardingButton
          sx={{ height: s("66px") }}
          text={t("onboarding__button_nothanks")}
          onEnterPress={() => {
            startLogin()
            setTracking({ type: "mux", value: false })
            setTracking({ type: "mixpanel", value: false })
            setTracking({ type: "sentry", value: false })
            sdk
              .consentTracking({
                input: {
                  consent: "REVOKE",
                  service: "MIXPANEL",
                },
              })
              .catch((e) => {
                console.log(e)
                captureSentryException(e)
              })
          }}
        />
        <OnboardingButton
          focusKey={OnboardingFocusKeys.PrivacySettings}
          sx={{ height: s("66px") }}
          text={t("tracking__advanced_settings_header")}
          onEnterPress={() => setPrivacyOpen(true)}
        />
      </Box>
    </FocusContext.Provider>
  )
}
